<template>
    <tailwind-layout title="Reference Email Stats">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Reference Email Statistics</h1>
                <p class="mt-1 text-sm text-gray-600">Track email invitations and click rates for tenant references.</p>
            </header>
            <div class="overflow-hidden">
            <!-- Aggregate Stats Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 px-4">
                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-sm font-medium text-gray-500">Current Landlord</h3>
                    <div class="mt-2">
                        <div class="text-2xl font-semibold text-gray-900">{{ stats.current_landlord.sent }}</div>
                        <div class="text-sm text-gray-500">{{ stats.current_landlord.clicked }} clicked ({{ stats.current_landlord.click_rate }}%)</div>
                    </div>
                </div>

                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-sm font-medium text-gray-500">Real Estate Agent</h3>
                    <div class="mt-2">
                        <div class="text-2xl font-semibold text-gray-900">{{ stats.real_estate_agent.sent }}</div>
                        <div class="text-sm text-gray-500">{{ stats.real_estate_agent.clicked }} clicked ({{ stats.real_estate_agent.click_rate }}%)</div>
                    </div>
                </div>

                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-sm font-medium text-gray-500">Previous Landlord</h3>
                    <div class="mt-2">
                        <div class="text-2xl font-semibold text-gray-900">{{ stats.previous_landlord.sent }}</div>
                        <div class="text-sm text-gray-500">{{ stats.previous_landlord.clicked }} clicked ({{ stats.previous_landlord.click_rate }}%)</div>
                    </div>
                </div>

                <div class="bg-blue-50 rounded-lg shadow p-6 border-2 border-blue-500">
                    <h3 class="text-sm font-bold text-blue-900">Total</h3>
                    <div class="mt-2">
                        <div class="text-2xl font-semibold text-blue-900">{{ stats.total.sent }}</div>
                        <div class="text-sm text-blue-700 font-medium">{{ stats.total.clicked }} clicked ({{ stats.total.click_rate }}%)</div>
                    </div>
                </div>
            </div>

            <!-- Reference Emails Table -->
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col" class="px-6 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th scope="col" class="px-6 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                                Reference Type
                                            </th>
                                            <th scope="col" class="px-6 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                                Sent At
                                            </th>
                                            <th scope="col" class="px-6 py-3.5 text-center text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th scope="col" class="px-6 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                                Signed Up As
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="bg-white divide-y divide-gray-200">
                                        <tr v-for="(email, index) in referenceEmails.data" :key="email.id"
                                            :class="index % 2 === 0 ? 'bg-gray-50' : 'bg-white'">
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {{ email.email }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {{ email.reference_type }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {{ email.sent_at }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-center">
                                                <span v-if="email.clicked" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    ✓ Clicked
                                                </span>
                                                <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    Not Clicked
                                                </span>
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {{ email.user_name || '-' }}
                                            </td>
                                        </tr>
                                        <tr v-if="!referenceEmails.data.length">
                                            <td colspan="5" class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                                No reference emails sent yet.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Mobile view -->
                                <div class="lg:hidden p-4 space-y-4">
                                    <div v-for="email in referenceEmails.data" :key="email.id" class="bg-white border rounded-lg p-4 shadow">
                                        <div class="space-y-2">
                                            <div class="flex justify-between items-start">
                                                <span class="text-sm font-medium text-gray-900">{{ email.email }}</span>
                                                <span :class="email.clicked ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium">
                                                    {{ email.clicked ? '✓ Clicked' : 'Not Clicked' }}
                                                </span>
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                <span class="font-medium">Type:</span> {{ email.reference_type }}
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                <span class="font-medium">Sent:</span> {{ email.sent_at }}
                                            </div>
                                            <div v-if="email.user_name" class="text-sm text-gray-600">
                                                <span class="font-medium">User:</span> {{ email.user_name }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!referenceEmails.data.length" class="text-center text-gray-500 text-sm">
                                        No reference emails sent yet.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <pagination class="mt-6" :links="referenceEmails.links" :results="referenceEmails" />
        </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { defineProps } from 'vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Pagination from '@/Shared/Pagination.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

defineProps({
    referenceEmails: Object,
    stats: Object,
})
</script>

